import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Lock, Link2, Pause, Play, Trash2, Database, Power, Boxes, Github, ExternalLink, GitBranch } from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { useAuth } from "@/contexts/AuthContext";
import {
  getIntelligenceProfile, pauseLearning, setPersonalization,
  disconnectPlatform, deleteIntelligenceData, deleteIntelligenceProfile, getConnectors,
  getGithubStatus, getGithubInstallUrl, getGithubManageUrl, disconnectGithub, getGithubReconnectUrl,
} from "@/lib/api";

const CONNECTOR_STATE = {
  CONNECTED: { label: "Connected", color: "#22e58a" },
  AVAILABLE: { label: "Available", color: "#22d3ee" },
  AUTHORIZATION_PENDING: { label: "Authorization Pending", color: "#ffb000" },
  COMING_SOON: { label: "Coming Soon", color: "#5a6b7a" },
  ERROR: { label: "Reconnect", color: "#ff3b5c" },
};

const GithubCard = ({ connector }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const st = CONNECTOR_STATE[connector.state] || CONNECTOR_STATE.AVAILABLE;
  const effState = status ? (status.connected ? "CONNECTED" : "AVAILABLE") : connector.state;
  const pill = CONNECTOR_STATE[effState] || st;

  const load = () => {
    if (!user) { setStatus({ connected: false }); return; }
    getGithubStatus().then(setStatus).catch(() => setStatus({ connected: false }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("github");
    if (p === "connected") toast.success("GitHub connected. CoreText now has read-only access to the repositories you selected.");
    else if (p === "conflict") toast.error("This GitHub installation is already connected to another CoreText account. Disconnect it from that account before connecting it here.");
    else if (p === "error") toast.error("GitHub connection didn't complete. Please try again.");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const connect = async () => {
    setBusy(true);
    try { const { url } = await getGithubInstallUrl(); window.location.assign(url); }
    catch { toast.error("Couldn't start GitHub connection."); setBusy(false); }
  };
  const manage = async () => {
    try { const { url } = await getGithubManageUrl(); window.open(url, "_blank", "noopener"); }
    catch { toast.error("Couldn't open GitHub settings."); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { const r = await disconnectGithub(); toast.success(r.message || "GitHub disconnected."); load(); }
    catch { toast.error("Disconnect failed."); }
    finally { setBusy(false); }
  };
  const reconnect = async () => {
    setBusy(true);
    try { const { url } = await getGithubReconnectUrl(); window.location.assign(url); }
    catch { toast.error("Couldn't start GitHub reconnect."); setBusy(false); }
  };

  const connected = status?.connected;
  return (
    <div className="sm:col-span-2 glass rounded-xl p-4" data-testid="connector-github">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Github size={18} className="text-white shrink-0" />
          <div className="min-w-0">
            <p className="font-display font-bold text-sm text-white truncate">{connector.name}</p>
            <p className="font-mono text-[10px] text-[var(--muted)] mt-0.5 leading-snug">{connector.note}</p>
          </div>
        </div>
        <span className="font-mono text-[9px] tracking-[0.12em] px-2 py-1 rounded-md border shrink-0"
          style={{ color: pill.color, borderColor: `${pill.color}66` }}
          data-testid="connector-state-github">
          {pill.label.toUpperCase()}
        </span>
      </div>

      {!user ? (
        <p className="font-mono text-[11px] text-[var(--muted)] mt-3">Sign in to connect your GitHub account.</p>
      ) : status === null ? (
        <div className="flex items-center gap-2 font-mono text-xs text-neon-cyan mt-3"><Loader2 className="animate-spin" size={14} /> Checking connection…</div>
      ) : !connected ? (
        <div className="mt-3">
          <button onClick={connect} disabled={busy} data-testid="github-connect-btn"
            className="inline-flex items-center gap-2 font-mono text-xs px-4 py-2 rounded-lg bg-white text-black font-bold hover:bg-white/90 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Github size={14} />}
            {busy ? "Opening GitHub…" : "Connect GitHub"}
          </button>
          <p className="font-mono text-[10px] text-[var(--muted)] mt-2">You choose exactly which repositories CoreText can read. Read-only (metadata + contents). No write access.</p>
          <p className="font-mono text-[10px] text-[var(--muted)] mt-2">
            Already installed CoreText on GitHub?{" "}
            <button onClick={reconnect} disabled={busy} data-testid="github-reconnect-btn"
              className="text-neon-cyan underline underline-offset-2 hover:text-neon-cyanBright disabled:opacity-60">
              Reconnect it
            </button>{" "}
            without reinstalling.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2 font-mono text-[11px] text-white">
            <span className={status.suspended ? "text-neon-amber" : "text-neon-green"}>●</span>
            {status.account_login || "GitHub account"}
            {status.suspended ? (
              <span className="text-neon-amber">· suspended on GitHub</span>
            ) : status.repos_available ? (
              <span className="text-[var(--muted)]">· {status.repository_selection === "all" ? "all repositories" : `${status.repositories?.length ?? 0} repositor${(status.repositories?.length ?? 0) === 1 ? "y" : "ies"}`}</span>
            ) : (
              <span className="text-[var(--muted)]">· repository details temporarily unavailable</span>
            )}
          </div>
          {status.repos_available && status.repositories?.length > 0 && (
            <ul className="space-y-1" data-testid="github-repo-list">
              {status.repositories.slice(0, 8).map((r) => (
                <li key={r.full_name} className="flex items-center gap-2 font-mono text-[11px] text-[var(--muted)]">
                  <GitBranch size={12} className="text-neon-cyan shrink-0" />
                  <span className="truncate">{r.full_name}</span>
                  {r.private && <span className="text-[9px] px-1 rounded border border-[var(--line)]">PRIVATE</span>}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={manage} data-testid="github-manage-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] px-3 py-1.5 rounded-lg border border-[var(--line)] text-white hover:border-neon-cyan/60 transition-colors">
              <ExternalLink size={12} /> Manage repositories
            </button>
            <button onClick={disconnect} disabled={busy} data-testid="github-disconnect-btn"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] px-3 py-1.5 rounded-lg border border-neon-red/40 text-neon-red hover:bg-neon-red/10 transition-colors disabled:opacity-60">
              <Power size={12} /> Disconnect
            </button>
          </div>
          <p className="font-mono text-[10px] text-[var(--muted)] leading-snug">
            "Manage repositories" opens GitHub to change which repos are shared. "Disconnect" removes CoreText's stored link — the GitHub App install stays until you remove it in GitHub.
          </p>
        </div>
      )}
    </div>
  );
};

const ConnectorStatusCenter = () => {
  const [reg, setReg] = useState(null);
  const [filter, setFilter] = useState("ALL");
  useEffect(() => { getConnectors().then((d) => setReg(d.registry)).catch(() => setReg([])); }, []);
  const cats = reg ? ["ALL", ...reg.map((c) => c.category)] : ["ALL"];
  const shown = reg ? (filter === "ALL" ? reg : reg.filter((c) => c.category === filter)) : [];
  return (
    <section className="glass neon-border rounded-2xl p-6 md:p-7 mb-6" data-testid="connector-status-center">
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-neon-cyan/50 glow-cyan shrink-0"><Boxes size={20} className="text-neon-cyan" /></span>
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Connectors</h2>
          <p className="font-mono text-sm text-[var(--muted)] mt-1 max-w-xl leading-relaxed">
            Connect your tools to make CoreText smarter about how you build. Each status is shown honestly — we only expose a connection when it can actually complete.
          </p>
        </div>
      </div>
      {reg && reg.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="connector-filter">
          {cats.map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              className={`font-mono text-[10px] tracking-[0.1em] px-2.5 py-1 rounded-md border transition-colors ${filter === c ? "border-neon-cyan/60 text-neon-cyan bg-neon-cyan/10" : "border-[var(--line)] text-[var(--muted)] hover:text-white"}`}
              data-testid={`connector-filter-${c.replace(/[^A-Za-z]/g, "").toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      {reg === null ? (
        <div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-4"><Loader2 className="animate-spin" size={16} /> Loading connectors...</div>
      ) : (
        <div className="space-y-5">
          {shown.map((cat) => (
            <div key={cat.category}>
              <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-2">{cat.category}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {cat.connectors.map((c) => {
                  const st = CONNECTOR_STATE[c.state] || CONNECTOR_STATE.COMING_SOON;
                  if (c.id === "github" && c.actionable) {
                    return <GithubCard key={c.id} connector={c} />;
                  }
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-3 glass rounded-xl p-3.5" data-testid={`connector-${c.id}`}>
                      <div className="min-w-0">
                        <p className="font-display font-bold text-sm text-white truncate">{c.name}</p>
                        {c.note && <p className="font-mono text-[10px] text-[var(--muted)] mt-0.5 leading-snug">{c.note}</p>}
                      </div>
                      <span className="font-mono text-[9px] tracking-[0.12em] px-2 py-1 rounded-md border shrink-0"
                        style={{ color: st.color, borderColor: `${st.color}66` }} data-testid={`connector-state-${c.id}`}>
                        {st.label.toUpperCase()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const Toggle = ({ on, onClick, testid, label, sub, Icon }) => (
  <div className="flex items-center justify-between gap-4 glass rounded-xl p-4">
    <div className="flex items-start gap-3">
      <Icon size={18} className="text-neon-cyan mt-0.5 shrink-0" />
      <div>
        <p className="font-display font-bold text-sm text-white">{label}</p>
        <p className="font-mono text-[11px] text-[var(--muted)] mt-0.5">{sub}</p>
      </div>
    </div>
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-12 h-6 rounded-full relative transition-colors shrink-0"
      style={{ background: on ? "#22d3ee" : "#2a2f3a" }}
      aria-pressed={on}
    >
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-[#05060a] transition-transform" style={{ transform: on ? "translateX(26px)" : "translateX(2px)" }} />
    </button>
  </div>
);

export default function Intelligence() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => getIntelligenceProfile().then(setP).catch(() => setP({ error: true }));
  useEffect(() => { if (user) load(); }, [user]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch { toast.error("Something went wrong. Try again."); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="relative text-[var(--text)] min-h-screen">
      <Nav />
      <main className="relative max-w-3xl mx-auto px-6 md:px-10 pt-28 pb-16" data-testid="intelligence-page">
        <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan mb-3">// BUILD INTELLIGENCE &amp; PRIVACY</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mb-3">Build Intelligence</h1>
        <p className="font-mono text-sm text-[var(--muted)] mb-10 max-w-xl leading-relaxed">
          Coretext gets smarter the more you build — but only with your explicit permission. You control every connection and can delete your data at any time.
        </p>
        {children}
      </main>
      <Footer />
    </div>
  );

  if (user === undefined) return <Shell><div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20"><Loader2 className="animate-spin" size={18}/> Loading...</div></Shell>;

  if (!user) {
    return (
      <Shell>
        <div className="glass neon-border rounded-2xl p-10 text-center" data-testid="intelligence-auth-gate">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border border-neon-cyan/50 glow-cyan mb-6"><Lock size={26} className="text-neon-cyan" /></span>
          <h2 className="font-display text-2xl font-bold text-white">Sign in to manage Build Intelligence.</h2>
          <Link to="/login" data-testid="intelligence-signin" style={{ backgroundColor: "#22d3ee", color: "#05060a" }} className="mt-6 inline-flex rounded-lg font-display font-bold py-3 px-7 hover:glow-cyan transition-shadow">SIGN IN</Link>
        </div>
      </Shell>
    );
  }

  if (!p) return <Shell><div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20"><Loader2 className="animate-spin" size={18}/> Loading your profile...</div></Shell>;

  const platforms = p.authorized_platforms || [];
  const connected = platforms.length > 0;

  return (
    <Shell>
      <ConnectorStatusCenter />
      {/* Connection / authorization */}
      <section className="glass neon-border rounded-2xl p-6 md:p-7 mb-6" data-testid="intelligence-connect">
        <div className="flex items-start gap-3 mb-4">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-neon-cyan/50 glow-cyan shrink-0"><Link2 size={20} className="text-neon-cyan" /></span>
          <div>
            <h2 className="font-display text-2xl font-bold text-white">Connect build history</h2>
            <p className="font-mono text-sm text-[var(--muted)] mt-1 max-w-xl leading-relaxed">
              With your permission, Coretext analyzes your previous build activity to learn how you communicate with AI builders, spot recurring rebuild patterns, and personalize future audits.
            </p>
          </div>
        </div>

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-2">CORETEXT MAY ANALYZE AUTHORIZED DATA:</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-5" data-testid="intelligence-categories">
          {(p.categories_analyzed || []).map((c) => (
            <li key={c} className="font-mono text-xs text-[var(--muted)] flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-neon-cyan" /> {c}
            </li>
          ))}
        </ul>

        {connected ? (
          <div className="flex items-center gap-3 flex-wrap" data-testid="intelligence-connected">
            <span className="font-mono text-sm text-neon-green inline-flex items-center gap-2"><ShieldCheck size={16} /> Authorized: {platforms.join(", ")}</span>
            <button data-testid="intelligence-disconnect" disabled={busy} onClick={() => act(() => disconnectPlatform(platforms[0]), "Platform disconnected. Future retrieval stopped.")} className="font-mono text-sm inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neon-red/40 text-neon-red hover:bg-neon-red/10 transition-colors"><Power size={15}/> Disconnect</button>
          </div>
        ) : (
          <>
            <div
              data-testid="intelligence-authorize-pending"
              className="rounded-lg font-mono text-sm py-2.5 px-5 inline-flex items-center gap-2 border border-[var(--line)] text-[var(--muted)] bg-white/[0.02]"
            >
              <Link2 size={16} /> Build-history connectors · Coming soon
            </div>
            <p className="font-mono text-[11px] text-[var(--muted)] mt-3 leading-relaxed">
              CoreText does not currently connect to Emergent, Lovable, Bolt, Replit, Cursor, or v0, and
              cannot access your private history, prompts, credits, or usage on those platforms. GitHub is
              the only integration direction on our roadmap. Until a connector is live, CoreText personalizes
              audits only from your own CoreText activity.
            </p>
          </>
        )}
      </section>

      {/* Learned patterns / empty state */}
      <section className="glass rounded-2xl p-6 md:p-7 mb-6" data-testid="intelligence-profile-state">
        <h2 className="font-display text-xl font-bold text-white mb-3">Your Build Intelligence Profile</h2>
        {p.has_data ? (
          <p className="font-mono text-sm text-neon-green">{p.build_count} builds analyzed · patterns are personalizing your audits.</p>
        ) : (
          <p className="font-mono text-sm text-[var(--muted)] leading-relaxed" data-testid="intelligence-empty">
            No authorized build history analyzed yet. Once you connect a platform and data is available, Coretext will learn your recurring omissions, corrections, preservation preferences, and rebuild causes here — and use them to personalize future audits.
          </p>
        )}
      </section>

      {/* Controls */}
      <section className="space-y-3 mb-6">
        <Toggle
          testid="intelligence-personalization-toggle"
          on={p.personalization_enabled}
          onClick={() => act(() => setPersonalization(!p.personalization_enabled), p.personalization_enabled ? "Personalization off — using standard auditing." : "Personalization on.")}
          label="Personalized auditing"
          sub="Use your authorized history to personalize audits. Off = standard Coretext auditing only."
          Icon={ShieldCheck}
        />
        <Toggle
          testid="intelligence-pause-toggle"
          on={!p.learning_paused}
          onClick={() => act(() => pauseLearning(!p.learning_paused), p.learning_paused ? "Learning resumed." : "Learning paused.")}
          label="Learning active"
          sub="Pause to stop Coretext from learning new patterns from your builds."
          Icon={p.learning_paused ? Play : Pause}
        />
      </section>

      {/* Danger zone */}
      <section className="glass rounded-2xl p-6 border" style={{ borderColor: "rgba(255,59,92,0.35)" }} data-testid="intelligence-danger">
        <p className="font-mono text-[11px] tracking-[0.2em] text-neon-red mb-4">DATA CONTROL</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="intelligence-delete-data" disabled={busy} onClick={() => act(deleteIntelligenceData, "Imported build-history data deleted.")} className="font-mono text-sm inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-neon-red/40 text-neon-red hover:bg-neon-red/10 transition-colors"><Database size={15}/> Delete imported data</button>
          <button data-testid="intelligence-delete-profile" disabled={busy} onClick={() => act(deleteIntelligenceProfile, "Build Intelligence Profile deleted.")} className="font-mono text-sm inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-neon-red/40 text-neon-red hover:bg-neon-red/10 transition-colors"><Trash2 size={15}/> Delete my profile</button>
        </div>
      </section>
    </Shell>
  );
}
